import { ICellRendererParams } from "@ag-grid-community/core";
import { WaterfallHybridFooter } from "features/waterfall/hybrid/HybridFooter";
import { WaterfallEditMenu } from "features/waterfall/review/WaterfallEditMenu";
import {
  useFundFamilySubEventRetrieveSuspense,
  useInvestmentWaterfallClassificationBulkUpdate,
  useWaterfallInvestmentClassificationTableDataRetrieveSuspense,
} from "gen/endpoints/fund-family-sub-event/fund-family-sub-event";
import { useGlobalEntityRetrieve } from "gen/endpoints/global-entity/global-entity";
import { AcceleratedPaydownTypeEnum } from "gen/model/acceleratedPaydownTypeEnum";
import { GenericTableCell } from "gen/model/genericTableCell";
import { InvestmentStatusEnum } from "gen/model/investmentStatusEnum";
import { InvestmentWaterfallClassificationBulkUpdateDataRequest } from "gen/model/investmentWaterfallClassificationBulkUpdateDataRequest";
import { InvestmentWaterfallClassificationUpdateData } from "gen/model/investmentWaterfallClassificationUpdateData";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { WaterfallClassificationMetadata } from "gen/model/waterfallClassificationMetadata";
import React, { useCallback } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormSimpleSelect } from "shared/components/input/select/SimpleSelect";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import {
  RenderCellValueFn,
  RenderCellValueProps,
} from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { setFormValueIfChanged } from "shared/utils/FormUtils";

const COLUMNS_TO_ENABLED_PAYDOWN_TYPES: {
  [key: string]: AcceleratedPaydownTypeEnum[];
} = {
  priority_paydown_cost: [
    AcceleratedPaydownTypeEnum.roc_only,
    AcceleratedPaydownTypeEnum.roc_then_pct_of_pref,
  ],
  pref_paydown_percentage: [AcceleratedPaydownTypeEnum.roc_then_pct_of_pref],
};

type IOptionallyDisabledIInputProps = {
  cellProps: RenderCellValueProps;
  params: ICellRendererParams;
};

const OptionallyDisabledInput: React.FC<IOptionallyDisabledIInputProps> = ({
  cellProps,
  params,
}) => {
  const { columnContext, cell } = cellProps;
  const columnType =
    columnContext.columnId as keyof InvestmentWaterfallClassificationUpdateData;
  const cellMetadata = cell.metadata! as WaterfallClassificationMetadata;
  const classificationId = cellMetadata.classification_id;
  const { control } =
    useFormContext<InvestmentWaterfallClassificationBulkUpdateDataRequest>();
  const rowData = useWatch({
    control,
    name: `classifications.${classificationId}`,
  });

  const isUnrealized = rowData.investment_status === "unrealized";
  if (!Object.keys(rowData).length || !classificationId) {
    return defaultDataTableRenderCellValue(cellProps, undefined, params);
  }

  if (!Object.keys(COLUMNS_TO_ENABLED_PAYDOWN_TYPES).includes(columnType)) {
    return defaultDataTableRenderCellValue(cellProps, !isUnrealized, params);
  }
  const columnEnabledForPaydownType =
    rowData.accelerated_paydown_type &&
    COLUMNS_TO_ENABLED_PAYDOWN_TYPES[columnType].includes(
      rowData.accelerated_paydown_type,
    );
  return defaultDataTableRenderCellValue(
    cellProps,
    !isUnrealized || !columnEnabledForPaydownType,
    params,
  );
};

const InvestmentStatusSelect: React.FC<{
  cell: GenericTableCell;
}> = ({ cell }) => {
  const { value, form_field } = cell;
  const text = (value as TextTableCellValue).text;
  const { resetField, setValue } =
    useFormContext<InvestmentWaterfallClassificationBulkUpdateDataRequest>();
  const cellMetadata = cell.metadata! as WaterfallClassificationMetadata;
  const classificationId = cellMetadata.classification_id;
  const form =
    useFormContext<InvestmentWaterfallClassificationBulkUpdateDataRequest>();
  const rowData = useWatch({
    control: form.control,
    name: `classifications.${classificationId}`,
  });
  const onInvestmentChange = useCallback(
    (investmentValue: string) => {
      if (!investmentValue) {
        return;
      }
      Object.keys(rowData).forEach((key) => {
        if (key === "investment_status") {
          setValue(
            `classifications.${classificationId}.${key}`,
            investmentValue as InvestmentStatusEnum,
          );
          return;
        }
        if (investmentValue !== "unrealized") {
          if (key === "accelerated_paydown_type") {
            setFormValueIfChanged(
              form,
              `classifications.${classificationId}.${key}`,
              AcceleratedPaydownTypeEnum.none as never,
            );
          } else {
            setFormValueIfChanged(
              form,
              `classifications.${classificationId}.${key}`,
              "0.00" as never,
            );
          }
        }
        if (investmentValue === "unrealized") {
          resetField(`classifications.${classificationId}.${key}`);
        }
      });
    },
    [classificationId, form, resetField, rowData, setValue],
  );

  return (
    <FormSimpleSelect
      name={form_field?.name ?? ""}
      title={form_field?.title ?? ""}
      shouldUnregister={true}
      defaultValue={text ?? undefined}
      onChangeValue={onInvestmentChange}
      variant="simple-table"
    >
      {form_field?.options?.map(({ value, rich_value }) => (
        <SelectOption key={`select_${value}`} value={value}>
          {rich_value}
        </SelectOption>
      ))}
    </FormSimpleSelect>
  );
};

const renderCellValue: RenderCellValueFn = (props, fieldDisabled, params) => {
  const { cell, columnContext } = props;
  if (!cell.form_field) {
    return defaultDataTableRenderCellValue(props, fieldDisabled, params);
  }
  return columnContext.columnId === "investment_status" ? (
    <InvestmentStatusSelect cell={cell} />
  ) : (
    <OptionallyDisabledInput cellProps={props} params={params} />
  );
};

export const HybridInvestmentClassificationsPage: React.FC = () => {
  const { t } = useTranslation(["common", "waterfall"]);

  const {
    fundFamilyEventId,
    fundFamilyId,
    fundFamilySubEventId,
    fundEntityId,
  } = useRequiredStringParams();

  const { data: fundFamilySubEventData } =
    useFundFamilySubEventRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
      fundFamilySubEventId,
    );

  const { navigateNext } = usePageWizard();

  const { data: tableData } =
    useWaterfallInvestmentClassificationTableDataRetrieveSuspense(
      fundFamilyId,
      fundFamilySubEventId,
      fundEntityId,
    );

  const { data: entityData } = useGlobalEntityRetrieve(
    fundFamilySubEventData.entity_id!,
    {
      query: { enabled: !!fundFamilySubEventData.entity_id },
    },
  );

  const { mutateAsync: bulkUpdateClassifications } =
    useInvestmentWaterfallClassificationBulkUpdate();

  const onSave = useCallback(
    async (data: InvestmentWaterfallClassificationBulkUpdateDataRequest) => {
      await bulkUpdateClassifications({
        fundFamilyId,
        fundFamilySubEventId,
        fundEntityId,
        data,
      });
    },
    [
      fundFamilyId,
      fundFamilySubEventId,
      fundEntityId,
      bulkUpdateClassifications,
    ],
  );

  const form =
    useForm<InvestmentWaterfallClassificationBulkUpdateDataRequest>();

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        workflowName={t("waterfall:waterfallClassifications.hybridWaterfall")}
        title={t(
          "waterfall:waterfallClassifications.status.confirmInvestmentStatus",
        )}
        helperText={t(
          "waterfall:waterfallClassifications.status.pageDescription",
        )}
        badge={
          entityData
            ? {
                color: "default",
                text: entityData.legal_name,
              }
            : undefined
        }
        actions={
          <WaterfallEditMenu
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            fundFamilySubEventId={fundFamilySubEventId}
            fundEntityId={fundEntityId}
            runWaterfall={false}
            isEqualizedView={undefined}
          />
        }
      />
      <PageBody>
        <Form form={form} onSubmit={onSave} onSuccess={navigateNext}>
          <TableBox isFullWidth={true}>
            <TableBoxHeader>
              <TableBoxTitle>
                {t(
                  "waterfall:waterfallClassifications.status.investmentClassification",
                )}
              </TableBoxTitle>
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable
                data={tableData}
                renderCellValue={renderCellValue}
                shouldUnregisterFormFields={false}
              />
            </TableBoxBody>
          </TableBox>
          <WaterfallHybridFooter />
        </Form>
      </PageBody>
    </PageContent>
  );
};
